import { useCallback, useEffect, useRef } from "react";
import type { MutableRefObject } from "react";
import type { NodeApi, TreeApi } from "react-arborist";
import type { TreeNodeData } from "../types";

interface UseTreeSelectionOptions {
  selectedKeys?: string[];
  defaultSelectedKeys?: string[];
  onSelect?: (keys: string[], info: { node: TreeNodeData }) => void;
  treeRef: MutableRefObject<TreeApi<TreeNodeData> | undefined>;
  selectable: boolean;
}

export const useTreeSelection = ({
  selectedKeys: controlledKeys,
  defaultSelectedKeys,
  onSelect,
  treeRef,
  selectable,
}: UseTreeSelectionOptions) => {
  const isControlled = controlledKeys !== undefined;
  const didApplyDefaults = useRef(false);

  useEffect(() => {
    if (!treeRef.current) return;

    if (isControlled) {
      const tree = treeRef.current;
      tree.deselectAll();
      controlledKeys.forEach(key => tree.selectMulti(key));
    } else if (!didApplyDefaults.current && defaultSelectedKeys?.length) {
      didApplyDefaults.current = true;
      const tree = treeRef.current;
      defaultSelectedKeys.forEach(key => tree.selectMulti(key));
    }
  }, [controlledKeys, isControlled, defaultSelectedKeys, treeRef]);

  const handleSelect = useCallback(
    (nodes: NodeApi<TreeNodeData>[]) => {
      if (!selectable || !onSelect) return;
      const lastNode = nodes[nodes.length - 1];
      if (!lastNode) return;

      onSelect(
        nodes.map(n => n.id),
        { node: lastNode.data }
      );
    },
    [onSelect, selectable]
  );

  return { handleSelect };
};
